import { isNull } from 'lodash';
import moment from 'moment-timezone';

import { Nullable } from '~globals/types/commons';
import { PaginationRequestParams } from '~globals/types/api';
import { VehicleFilter, VehicleSearchItem } from '~services/vehicle/types';
import { WarehouseExtended } from '~services/warehouse/types';
import { ItemExtended } from '~services/item/types';
import { AuthUserSubscription } from '~services/auth/types';
import {
  PointAddressSelectorData,
  PointAddressSelectorLabels,
} from '~components/PointAddressSelector/types';
import { isOnAgendaOrder } from '~utils/order';
import { isCurrentLoggedCompany } from '~utils/partnerCompany';
import { pluralize } from '~utils/commons';
import { parseSkillsStringToArray } from '~utils/vehicle';

export const validateEndDateIsAftertartDate = (
  startDate: Date,
  endDate: Date,
): boolean => {
  const transformStartDate = moment(startDate, moment.HTML5_FMT.DATE).startOf(
    'day',
  );

  const transformEndDate = moment(endDate, moment.HTML5_FMT.DATE).startOf(
    'day',
  );

  return transformEndDate.isAfter(transformStartDate, 'day');
};

export const getDefaultScheduleStartDate = (): Date =>
  new Date(moment().format());

export const getDefaultVehiclesRequestPayload = (
  scheduleDateTime: string,
): PaginationRequestParams<VehicleFilter> => ({
  pagination: {
    page: 1,
    sizePerPage: 50,
  },
  filters: {
    enabled: null,
    query: null,
    scheduleDateTime,
  },
});

export const getPointDataByWarehouse = (
  warehouse: Nullable<WarehouseExtended>,
): Nullable<PointAddressSelectorData> => {
  if (!warehouse) return null;

  return {
    id: warehouse.warehouseId,
    address: warehouse.address ?? '',
    latitude: warehouse.latitude,
    longitude: warehouse.longitude,
  };
};

export const getDisabledVehicle = (vehicle: VehicleSearchItem): boolean =>
  !(vehicle.isAvailable && vehicle.isOpenForWork);

export const getDisabledItem = (
  order: ItemExtended,
  user: Nullable<AuthUserSubscription>,
): boolean => {
  if (isOnAgendaOrder(order)) return true;

  return (
    !isNull(order.carrierCompanyId) &&
    !isCurrentLoggedCompany(order.carrierCompanyId, user?.companyId ?? null)
  );
};

export const getSelectedEntityDescription = (
  selecteds: Array<number | string>,
  entity: string,
  withSelected = true,
): string => {
  const countSelecteds = selecteds.length;

  const entityPluralize = pluralize({
    singular: entity,
    count: countSelecteds,
    includeCount: true,
  });

  if (!withSelected) return `(${entityPluralize})`;

  const selectedText = pluralize({
    singular: 'seleccionado',
    count: countSelecteds,
    includeCount: false,
  });

  return `(${entityPluralize} ${selectedText})`;
};

export const getUniqueTotalSkills = (
  items: ItemExtended[],
  vehicles: VehicleSearchItem[],
): { items: Set<string>; vehicles: Set<string> } => {
  const itemsSkills = new Set<string>();
  const vehiclesSkills = new Set<string>();

  items.forEach((item) => {
    const currentItemSkills = parseSkillsStringToArray(item.skillsNeeded);

    currentItemSkills.forEach((itemSkill) => itemsSkills.add(itemSkill));
  });

  vehicles.forEach((vehicle) => {
    const currentVehiclesSkills = parseSkillsStringToArray(vehicle.skillsList);

    currentVehiclesSkills.forEach((vehicleSkill) =>
      vehiclesSkills.add(vehicleSkill),
    );
  });

  return { items: itemsSkills, vehicles: vehiclesSkills };
};

export const validatePresenceOfSkillsOfItemsInVehicle = (
  items: ItemExtended[],
  vehicles: VehicleSearchItem[],
): boolean => {
  const totalSkills = getUniqueTotalSkills(items, vehicles);

  const currentSelectedSkillsItems = Array.from(totalSkills.items);
  const currentSelectedSkillsVehicles = Array.from(totalSkills.vehicles);

  const copyCurrentSelectedSkillsVehicles = [...currentSelectedSkillsVehicles];

  for (
    let orderSkillIndex = 0;
    orderSkillIndex < currentSelectedSkillsItems.length;
    orderSkillIndex++
  ) {
    const vehicleSkillIndex = copyCurrentSelectedSkillsVehicles.indexOf(
      currentSelectedSkillsItems[orderSkillIndex],
    );

    if (vehicleSkillIndex === -1) return false;

    copyCurrentSelectedSkillsVehicles.splice(vehicleSkillIndex, 1);
  }

  return true;
};

const getPrefixPointAddressSelector = (type: 'starting' | 'arrival'): string =>
  type === 'arrival' ? 'llegada' : 'partida';

const getLabelPointAddressSelector = ({
  entity,
  type,
  isOptional,
}: {
  entity: string;
  type: 'starting' | 'arrival';
  isOptional: boolean;
}): string => {
  let baseLabel = `${entity} de ${getPrefixPointAddressSelector(type)}`;

  if (isOptional) baseLabel += ' (opcional)';

  return baseLabel;
};

export const getLabelsPointAddressSelector = ({
  type,
  isOptional,
}: {
  type: 'starting' | 'arrival';
  isOptional: boolean;
}): PointAddressSelectorLabels => ({
  warehouse: getLabelPointAddressSelector({
    entity: 'Depósito',
    type,
    isOptional,
  }),
  contact: getLabelPointAddressSelector({
    entity: 'Contacto',
    type,
    isOptional,
  }),
  address: getLabelPointAddressSelector({
    entity: 'Dirección',
    type,
    isOptional,
  }),
});
